import { useState, useCallback, useEffect, useMemo } from 'react';
import { Text, Button, Flex, Spinner, Badge } from '@radix-ui/themes';
import { Dialog } from '@/components/shared/dialog';
import Select, { type SelectOption } from '@/components/shared/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import styles from './VerifySnippetDialog.module.scss';

const ROOT_OPTION = 'No Collection';

const toOptionValue = (path: string) => (path ? path : ROOT_OPTION);
const toCollectionPath = (value: string) => (value === ROOT_OPTION ? '' : value);

interface CreateSnippetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCollectionPath: string;
  collectionPaths: string[];
  onCreateSnippet: (collectionPath: string, snippetName: string, isInternal: boolean) => Promise<void>;
  dialogClassName?: string;
}

export function CreateSnippetDialog({
  open,
  onOpenChange,
  defaultCollectionPath,
  collectionPaths,
  onCreateSnippet,
  dialogClassName,
}: CreateSnippetDialogProps) {
  const [snippetName, setSnippetName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCollectionPath, setSelectedCollectionPath] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const options = useMemo<SelectOption[]>(() => {
    const unique = new Set(collectionPaths.length > 0 ? collectionPaths : ['']);
    if (!unique.has('')) {
      unique.add('');
    }
    return Array.from(unique).map((path) => {
      const value = toOptionValue(path);
      return { label: value, value };
    });
  }, [collectionPaths]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const normalizedDefault = defaultCollectionPath.startsWith('/')
      ? defaultCollectionPath.slice(1)
      : defaultCollectionPath;
    const available = options.map((option) => toCollectionPath(option.value));
    const nextPath = available.includes(normalizedDefault)
      ? normalizedDefault
      : available[0] ?? '';
    setSelectedCollectionPath(nextPath);
  }, [open, defaultCollectionPath, options]);

  const handleCreate = useCallback(async () => {
    if (snippetName.trim() && !isLoading) {
      setIsLoading(true);
      try {
        await onCreateSnippet(selectedCollectionPath, snippetName.trim(), isInternal);
        toast.success('Snippet created successfully');
        setSnippetName('');
        setIsInternal(false);
        onOpenChange(false);
      } catch (error) {
        toast.error('Failed to create snippet. Please try again.');
        console.error('Error creating snippet:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [snippetName, isLoading, selectedCollectionPath, isInternal, onCreateSnippet, onOpenChange]);

  const handleCancel = useCallback(() => {
    if (!isLoading) {
      setSnippetName('');
      setSelectedCollectionPath('');
      setIsInternal(false);
      onOpenChange(false);
    }
  }, [isLoading, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && snippetName.trim() && !isLoading) {
        handleCreate();
      }
    },
    [snippetName, isLoading, handleCreate]
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !isLoading && onOpenChange(open)}>
      <Dialog.Content size="medium" className={dialogClassName ? `${styles.dialogContent} ${dialogClassName}` : styles.dialogContent}>
        <Dialog.Close className={styles.closeButton} disabled={isLoading} />
        <Flex direction="column" gap="6" p="5">
          <Flex direction="column" gap="1">
            <Text size="4" weight="medium" className={styles.title}>
              Create Snippet
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              Choose a collection and provide a name.
            </Text>
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Collection</Text>
            </label>
            <Select
              options={options}
              value={toOptionValue(selectedCollectionPath)}
              onChange={(value) => setSelectedCollectionPath(toCollectionPath(value))}
              allowSearch
              disabled={isLoading}
            />
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Snippet name</Text>
            </label>
            <input
              type="text"
              className={styles.input}
              value={snippetName}
              onChange={(e) => setSnippetName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter snippet name"
              disabled={isLoading}
              autoFocus
            />
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Access Type</Text>
            </label>
            <Flex align="center" gap="3">
              <Badge color={isInternal ? "blue" : "purple"} variant="soft" size="1">
                <Text size="1" weight="medium">
                  {isInternal ? "Internal" : "External"}
                </Text>
              </Badge>
              <Flex align="center" gap="2">
                <Text size="2" color="gray">External</Text>
                <Switch
                  checked={isInternal}
                  onCheckedChange={setIsInternal}
                  disabled={isLoading}
                />
                <Text size="2" color="gray">Internal</Text>
              </Flex>
            </Flex>
            <Text size="1" color="gray" style={{ marginTop: '4px' }}>
              Internal snippets are only accessible to your team. External snippets can be shared publicly.
            </Text>
          </Flex>

          <Flex gap="3" justify="end">
            <Button
              variant="ghost"
              size="2"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              <Text size="3" color="gray">
                Cancel
              </Text>
            </Button>
            <Button
              variant="solid"
              color="gray"
              size="2"
              highContrast
              onClick={handleCreate}
              disabled={!snippetName.trim() || isLoading}
              className={styles.verifyButton}
            >
              {isLoading ? (
                <>
                  <Spinner size="1" />
                  <Text size="3">Creating...</Text>
                </>
              ) : (
                <Text size="3">Create</Text>
              )}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
